import { useLocalStorage } from "@p0security/frontend-core";
import { Table as AntTable, TableRowSelection } from "@p0security/ui";
import type { TableColumnType, TableProps } from "@p0security/ui";
import classNames from "classnames";
import React, { SyntheticEvent, useCallback, useMemo } from "react";
import { Resizable, type ResizeCallbackData } from "react-resizable";

const isMouseEvent = (
  e: React.SyntheticEvent
): e is React.MouseEvent<HTMLElement> => {
  return e instanceof MouseEvent;
};

type ResizeHandler = (
  e: React.MouseEvent<HTMLElement>,
  data: ResizeCallbackData
) => void;

interface ResizableTableProps<T extends object> extends TableProps<T> {
  columns: ResizableTableColumnsType<T>;
  rowSelection?: Omit<TableRowSelection<T>, "columnWidth">;
  tableId: string;
}

const stopPropagation = (event: React.MouseEvent | React.SyntheticEvent) => {
  event.stopPropagation();
};

/**
 * A type that extends the antd TableColumnsType type,
 * but forces the width property to be a number.
 *
 * This is because the resizable library only works with
 * numeric widths.
 *
 * By default, columns in this table will be resizable
 * unless the optional preventResize prop is set to true
 */

interface BaseResizableTableColumnType<
  T extends object,
> extends TableColumnType<T> {
  width: number;
  preventResize?: boolean;
}

export type ResizableTableColumnType<T extends object> =
  BaseResizableTableColumnType<T> &
    ({ key: string } | { title: string }) & {
      minWidth?: number;
    };

export type ResizableTableColumnsType<RecordType extends object> =
  ResizableTableColumnType<RecordType>[];

const MIN_COLUMN_WIDTH = 50;
const DEFAULT_COLUMN_WIDTH = 100;
const MAX_COLUMN_WIDTH = 500;
const SELECT_BOX_COLUMN_WIDTH = 35;
// Width of the drag handle in pixels; the handle is offset by half its width to
// center it on the column edge.
const RESIZE_HANDLE_WIDTH = 10;

// These keys should be unique across the app
export const ResizableTableKeys = {
  frequentRequests: "frequent-requests",
  graph: "graph",
  jitStatistics: "jit-statistics",
  agenticEventLog: "agentic-activity",
};

const ResizableTitle: React.FC<
  Readonly<
    React.HTMLAttributes<any> & {
      onColumnResize: ResizeHandler;
      onResetWidth?: React.MouseEventHandler;
      preventResize?: boolean;
      width: number;
    }
  >
> = ({
  width,
  preventResize,
  onColumnResize: propOnResize,
  onResetWidth,
  className,
  ...restProps
}) => {
  // overflow-visible! overrides antd, which would otherwise clip the resize handle.
  const component = (
    <th className={classNames("overflow-visible!", className)} {...restProps} />
  );

  const onResize = useCallback(
    (e: SyntheticEvent, data: ResizeCallbackData) => {
      if (isMouseEvent(e)) propOnResize(e, data);
    },
    [propOnResize]
  );

  // if the column's width is smaller than the minimum
  // or larger than the maximum, resizing it will make
  // it visually "jump" on the screen when it immediately
  // resizes to the minimum/maximum width so we just
  // return a non-resizable component in that case
  if (isNaN(width) || preventResize) {
    return component;
  }

  return (
    <Resizable
      width={width}
      height={0}
      minConstraints={[MIN_COLUMN_WIDTH, MIN_COLUMN_WIDTH]}
      maxConstraints={[MAX_COLUMN_WIDTH, MAX_COLUMN_WIDTH]}
      // stopPropagation here prevents sort change during resize
      handle={
        // Width and edge offset both derive from RESIZE_HANDLE_WIDTH (offset =
        // -width/2 to center on the column edge). They're runtime-derived, so
        // they use inline styles rather than Tailwind classes.
        <span
          className="absolute bottom-0 z-[1] h-full cursor-col-resize"
          style={{
            width: RESIZE_HANDLE_WIDTH,
            insetInlineEnd: -RESIZE_HANDLE_WIDTH / 2,
          }}
          onClick={stopPropagation}
          onDoubleClick={onResetWidth}
        />
      }
      onResize={onResize}
      onResizeStart={stopPropagation}
      onResizeStop={stopPropagation}
      draggableOpts={{ enableUserSelectHack: true }}
    >
      {component}
    </Resizable>
  );
};

const toColumnKey = (column: ResizableTableColumnType<any>) => {
  return `${column.key ?? column.title}`;
};

/**
 * A table component that allows the columns to be resized.
 *
 * Takes the same props as the antd Table component, except that
 * column widths must be numbers.
 *
 * Any column that has a numeric width greater than 70 will be
 * resizable.  The 70 minimum is necessary to keep any sorting and filtering
 * options visible.
 *
 * Adapted from https://ant.design/components/table#table-demo-resizable-column
 */
export const ResizableTable = <T extends object>(
  props: ResizableTableProps<T>
) => {
  const { columns, components, tableId, className, ...restProps } = props;
  const widthsToKey = useMemo(
    () => Object.fromEntries(columns.map((c) => [toColumnKey(c), c.width])),
    [columns]
  );
  const [columnWidths, setColumnWidths] = useLocalStorage<
    Record<string, number>
  >(`resizable-table-columns-${tableId}`, widthsToKey);

  const handleResize = useCallback(
    (key: string): ResizeHandler =>
      (event, { node }) => {
        event.stopPropagation();
        const { parentElement } = node;
        if (!parentElement) return;
        const rect = parentElement.getBoundingClientRect();
        // delta is in rendered-pixel space. Under tableLayout:fixed + w-full the
        // browser may scale rendered column widths away from stored widths, so
        // normalize back to stored space before accumulating: a 10px mouse move
        // on a column rendered at 1.5× its stored width should add only ~6.7px
        // to the stored value, keeping the visual motion 1:1 with the cursor.
        const delta = event.clientX - (rect.x + rect.width);
        setColumnWidths((prev) => {
          const currentStored =
            prev[key] ?? widthsToKey[key] ?? DEFAULT_COLUMN_WIDTH;
          const normalizedDelta =
            rect.width > 0 ? delta * (currentStored / rect.width) : delta;
          return {
            ...prev,
            [key]: Math.min(
              Math.max(currentStored + normalizedDelta, MIN_COLUMN_WIDTH),
              MAX_COLUMN_WIDTH
            ),
          };
        });
      },
    [setColumnWidths, widthsToKey]
  );

  const handleResetWidth = useCallback(
    (
      key: string,
      column: ResizableTableColumnType<T>
    ): React.MouseEventHandler =>
      (event) => {
        event.stopPropagation();
        setColumnWidths((prev) => ({
          ...prev,
          [key]: column.width,
        }));
      },
    [setColumnWidths]
  );

  const onHeaderCell = useCallback(
    (key: string, column: ResizableTableColumnType<T>) => () => {
      const storedWidth =
        columnWidths[key] ?? column.width ?? DEFAULT_COLUMN_WIDTH;
      return {
        onColumnResize: handleResize(key),
        onResetWidth: handleResetWidth(key, column),
        preventResize: column.preventResize,
        width: Math.max(column.minWidth ?? 0, storedWidth),
      };
    },
    [columnWidths, handleResetWidth, handleResize]
  );

  const mergedColumns = useMemo(
    () =>
      columns.map<ResizableTableColumnType<T>>((column) => {
        const key = toColumnKey(column);
        const width = columnWidths[key] ?? column.width;

        return {
          ...column,
          width: Math.max(column.minWidth ?? 0, width),
          onHeaderCell: onHeaderCell(key, column),
        };
      }),
    [columnWidths, columns, onHeaderCell]
  );

  return (
    <AntTable<T>
      // Turn off the tooltip by default, but allow it to be overridden
      showSorterTooltip={false}
      {...restProps}
      components={{ ...components, header: { cell: ResizableTitle } }}
      columns={mergedColumns}
      // Set the column width of row selection, if row selection is enabled for this table
      rowSelection={
        "rowSelection" in restProps
          ? { ...restProps.rowSelection, columnWidth: SELECT_BOX_COLUMN_WIDTH }
          : undefined
      }
      // tableLayout: fixed makes the per-column pixel widths (set on each header
      // cell) authoritative instead of content-driven; the table itself fills its
      // container via w-full.
      // See https://developer.mozilla.org/en-US/docs/Web/CSS/table-layout
      tableLayout="fixed"
      className={classNames("w-full", className)}
    />
  );
};
